import React from 'react';

import type { MediaWrapperTypeEnum } from '@/utils/aspectRatioUtils';
import { MediaWrapperContainerTypeEnum } from '@/utils/aspectRatioUtils';

import type { CardProps } from '@/cms/Card';
import { Card } from '@/cms/Card';
import { CMSSection } from '@/cms/CMSSection';
import type { ProductCardProps } from '@/cms/ProductCard';
import { ProductCard } from '@/cms/ProductCard';
import { Container } from '@/components/Container';
import type {
  ContentfulContentGroupFragment,
  ContentfulFreeTextContentFragment,
  ContentfulHeadingFragment,
  ContentfulNavQuickLinksFragment,
  ContentfulPlaceholderServiceFragment,
} from '@/graphql/contentful/api';
import type { CmsExtraDataRecord } from '@/integrations/contentful';
import { generateSectionKey } from '@/integrations/contentful';
import type {
  ContentfulData,
  ContentType,
} from '@/integrations/contentful/types';
import { generateDataTestId } from '@/lib/tests';
import type { WithHTMLProp } from '@/lib/types';

import type { MediaWrapperProps } from '../MediaWrapper';
import { MediaWrapper } from '../MediaWrapper';

import * as S from './DoubleCard.styles';

type DoubleCardProps = ContentfulContentGroupFragment & {
  extraDataRecord?: CmsExtraDataRecord;
} & WithHTMLProp<'className'>;

export const DoubleCard = ({
  className,
  extraDataRecord,
  itemsCollection,
}: DoubleCardProps) => {
  return (
    <CMSSection
      className={className}
      {...generateDataTestId('DoubleCard', 'section')}
      data-test-id='DoubleCardSection'
    >
      <Container>
        <S.Wrapper data-test-id='DoubleCardWrapper'>
          {itemsCollection?.items.filter(Boolean).map((item, i, array) => {
            const props = (item ? { ...item } : null) as ItemData;
            if (props) {
              props.preload = true;
            }
            const renderedItem = renderCollectionItem(props, extraDataRecord);
            return (
              renderedItem && (
                <S.Col
                  key={`${generateSectionKey((item as ContentfulData)!, i)}`}
                  data-test-id={`DoubleCardCol-${i}`}
                  md={12 / array.length}
                >
                  {renderCollectionItem(item! as ItemData, extraDataRecord)}
                </S.Col>
              )
            );
          })}
        </S.Wrapper>
      </Container>
    </CMSSection>
  );
};

export interface NavQuickLinksProps extends ContentfulNavQuickLinksFragment {
  mediaWrapperType?: string;
  position?: string;
  preload?: boolean;
}

export interface PlaceholderServiceProps extends ContentfulPlaceholderServiceFragment {
  preload?: boolean;
}
export type ItemData =
  | CardProps
  | ProductCardProps
  | NavQuickLinksProps
  | PlaceholderServiceProps
  | null;

export const renderCollectionItem = (
  data: ItemData,
  extraDataRecord?: CmsExtraDataRecord,
  cardGroupType = MediaWrapperContainerTypeEnum.DoubleCard,
  mediaWrapperType?: MediaWrapperTypeEnum,
) => {
  let props = null;
  const typename = data?.__typename as ContentType;
  const sysId = data?.sys?.id;
  const extraData = extraDataRecord ? extraDataRecord[sysId ?? ''] : undefined;
  const _mediaWrapperType = mediaWrapperType ?? cardGroupType;

  switch (typename) {
    case 'Card':
      props = data as CardProps;
      return (
        <Card
          {...props}
          cardGroupType={cardGroupType}
          data-test-id='DoubleCardItem-Card'
          extraData={extraData?.extraData}
        />
      );
    case 'ImageWithFocalPoint':
      props = data as MediaWrapperProps;
      return (
        <MediaWrapper
          {...props}
          data-test-id='DoubleCardItem-MediaWrapper'
          mediaWrapperType={_mediaWrapperType as MediaWrapperTypeEnum}
        />
      );
    case 'ProductCard':
      props = data as ProductCardProps;
      return (
        <ProductCard
          autoLoadCTA={true}
          {...props}
          data-test-id='DoubleCardItem-ProductCard'
          productData={extraData?.extraData?.productData[0]}
        />
      );
    case 'PlaceholderService':
      props = data as ContentfulPlaceholderServiceFragment;
      return <S.PlaceholderServiceStyled {...props} />;
    case 'Heading':
      props = data as ContentfulHeadingFragment;
      return <S.HeadingStyled Heading={props} />;
    case 'FreeTextContent':
      props = data as ContentfulFreeTextContentFragment;
      return <S.FreeTextContentStyled {...props} />;
    default:
      return null;
  }
};

DoubleCard.displayName = 'DoubleCard';
